import type { MouseEvent, ReactElement } from 'react';
import './GridRowActions.css';

export interface GridRowAction<T> {
  id: string;
  label: string;
  /** 'icon' (default) renders the external-link glyph; 'button' renders a labeled pill button. */
  variant?: 'icon' | 'button';
  /** Custom glyph for the 'icon' variant; defaults to the external-link glyph. */
  icon?: ReactElement;
  /** Which pinned actions column hosts this action; defaults to 'left'. */
  side?: 'left' | 'right';
  /** Room a 'button' action needs in its pinned column (px); defaults to 52. */
  buttonWidth?: number;
  className?: string;
  ariaLabel?: (row: T) => string;
  isVisible?: (row: T) => boolean;
  /** Shown as plain text in the action's place when isVisible is false (e.g. why it is unavailable). */
  fallbackText?: (row: T) => string | null;
  onPrefetch?: (row: T) => void | Promise<void>;
  onSelect: (row: T) => void;
}

export function isGridInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(
    'button, a, input, select, textarea, [role="button"], .ag-selection-checkbox, .grid-row-actions',
  ));
}

export function GridRowActions<T>({
  row,
  actions,
}: {
  row: T;
  actions: GridRowAction<T>[];
}): ReactElement | null {
  const visibleActions = actions.filter((action) => action.isVisible?.(row) ?? true);
  const notes = actions
    .filter((action) => !(action.isVisible?.(row) ?? true))
    .map((action) => ({ id: action.id, text: action.fallbackText?.(row) ?? null }))
    .filter((note): note is { id: string; text: string } => Boolean(note.text));
  if (visibleActions.length === 0 && notes.length === 0) return null;

  const stopPropagation = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="grid-row-actions">
      {notes.map((note) => (
        <span key={note.id} className="grid-row-action-note" title={note.text}>{note.text}</span>
      ))}
      {visibleActions.map((action) => (
        <button
          key={action.id}
          type="button"
          className={[
            action.variant === 'button' ? 'grid-row-action-button' : 'grid-row-action',
            action.className,
          ].filter(Boolean).join(' ')}
          title={action.label}
          aria-label={action.ariaLabel?.(row) ?? action.label}
          onPointerEnter={() => {
            Promise.resolve(action.onPrefetch?.(row)).catch((error: unknown) => {
              console.warn(`Could not prefetch ${action.label}`, error);
            });
          }}
          onFocus={() => {
            Promise.resolve(action.onPrefetch?.(row)).catch((error: unknown) => {
              console.warn(`Could not prefetch ${action.label}`, error);
            });
          }}
          onMouseDown={stopPropagation}
          onClick={(event) => {
            stopPropagation(event);
            action.onSelect(row);
          }}
        >
          {action.variant === 'button' ? action.label : action.icon ?? (
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 3h7v7" />
              <path d="M10 14L21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          )}
        </button>
      ))}
    </div>
  );
}
